import { useMemo, useState } from "react";
import { CheckCircle2, Paperclip, RefreshCw } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import AdminLayout from "@/components/layout/AdminLayout";
import {
  useReactivationRequests,
  type ReactivationRequest,
} from "@/hooks/useReactivationRequests";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const formatDateTime = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()} ${hh}:${mm}`;
};

const StatusBadge = ({ status }: { status: string }) => {
  if (status === "approved") {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
        Approved
      </span>
    );
  }
  if (status === "rejected") {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-500/15 text-red-400 border border-red-500/30">
        Rejected
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-500/15 text-amber-400 border border-amber-500/30">
      Pending
    </span>
  );
};

interface CardProps {
  request: ReactivationRequest;
  onApprove: (r: ReactivationRequest) => void;
  onReject: (r: ReactivationRequest) => void;
}

function RequestCard({ request, onApprove, onReject }: CardProps) {
  const requesterName = `${request.requester_first_name} ${request.requester_last_name}`.trim() || "Unknown user";
  const cooldownActive =
    request.cooldown_expires_at && new Date(request.cooldown_expires_at).getTime() > Date.now();

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-md p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-bold text-white">{request.company_name}</span>
          <StatusBadge status={request.status} />
        </div>
        <span className="text-xs text-slate-400 shrink-0">{formatDateTime(request.created_at)}</span>
      </div>

      <div className="text-xs text-slate-400">
        Requested by {requesterName} ({request.requester_email || "no email"})
      </div>

      <div className="text-sm text-slate-300 whitespace-pre-wrap">{request.reason}</div>

      {request.attachment_name && request.attachment_url && (
        <a
          href={request.attachment_url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 text-xs text-amber-400 hover:text-amber-300 hover:underline"
        >
          <Paperclip className="h-3.5 w-3.5" />
          {request.attachment_name}
        </a>
      )}

      {cooldownActive && request.cooldown_expires_at && (
        <div className="text-xs text-amber-400">
          Company can resubmit after {formatDateTime(request.cooldown_expires_at)}
        </div>
      )}

      {request.superuser_notes && request.status !== "pending" && (
        <div className="pt-2 border-t border-slate-800">
          <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">
            Admin notes
          </div>
          <div className="text-sm text-slate-300 whitespace-pre-wrap">{request.superuser_notes}</div>
        </div>
      )}

      {request.status === "pending" && (
        <div className="flex items-center gap-2 pt-2 border-t border-slate-800">
          <Button
            size="sm"
            onClick={() => onApprove(request)}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            Approve
          </Button>
          <Button
            size="sm"
            variant="destructive"
            onClick={() => onReject(request)}
          >
            Reject
          </Button>
        </div>
      )}
    </div>
  );
}

export default function Requests() {
  const queryClient = useQueryClient();
  const { requests, isLoading, error, refetch } = useReactivationRequests();

  const [approveTarget, setApproveTarget] = useState<ReactivationRequest | null>(null);
  const [rejectTarget, setRejectTarget] = useState<ReactivationRequest | null>(null);
  const [approveNotes, setApproveNotes] = useState("");
  const [rejectNotes, setRejectNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const { pending, reviewed } = useMemo(() => {
    const p: ReactivationRequest[] = [];
    const r: ReactivationRequest[] = [];
    for (const req of requests) {
      if (req.status === "pending") p.push(req);
      else if (req.status === "approved" || req.status === "rejected") r.push(req);
    }
    return { pending: p, reviewed: r };
  }, [requests]);

  const closeApprove = () => {
    if (submitting) return;
    setApproveTarget(null);
    setApproveNotes("");
  };
  const closeReject = () => {
    if (submitting) return;
    setRejectTarget(null);
    setRejectNotes("");
  };

  const handleApprove = async () => {
    if (!approveTarget) return;
    setSubmitting(true);
    try {
      const { error: rpcErr } = await supabase.rpc("approve_company_reactivation_request", {
        p_request_id: approveTarget.id,
        p_notes: approveNotes.trim() || undefined,
      });
      if (rpcErr) throw rpcErr;

      toast.success("Request approved — company reactivated");
      queryClient.invalidateQueries({ queryKey: ["reactivation-requests"] });
      queryClient.invalidateQueries({ queryKey: ["companies"] });
      queryClient.invalidateQueries({ queryKey: ["pending-requests-count"] });

      // Fire-and-forget email notification
      void supabase.functions
        .invoke("send-company-reactivation-decision", {
          body: {
            to_email: approveTarget.requester_email,
            to_name: `${approveTarget.requester_first_name} ${approveTarget.requester_last_name}`.trim(),
            company_name: approveTarget.company_name,
            decision: "approved",
            notes: approveNotes.trim() || null,
          },
        })
        .catch(() => {
          /* swallow */
        });

      setApproveTarget(null);
      setApproveNotes("");
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Failed to approve request";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!rejectTarget) return;
    const notes = rejectNotes.trim();
    if (!notes) {
      toast.error("A reason is required to reject this request");
      return;
    }
    setSubmitting(true);
    try {
      const { error: rpcErr } = await supabase.rpc("reject_company_reactivation_request", {
        p_request_id: rejectTarget.id,
        p_notes: notes,
      });
      if (rpcErr) throw rpcErr;

      toast.success("Request rejected");
      queryClient.invalidateQueries({ queryKey: ["reactivation-requests"] });
      queryClient.invalidateQueries({ queryKey: ["pending-requests-count"] });

      void supabase.functions
        .invoke("send-company-reactivation-decision", {
          body: {
            to_email: rejectTarget.requester_email,
            to_name: `${rejectTarget.requester_first_name} ${rejectTarget.requester_last_name}`.trim(),
            company_name: rejectTarget.company_name,
            decision: "rejected",
            notes,
          },
        })
        .catch(() => {
          /* swallow */
        });

      setRejectTarget(null);
      setRejectNotes("");
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Failed to reject request";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const renderList = (list: ReactivationRequest[], emptyMessage: string) => {
    if (isLoading) {
      return (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-40 w-full bg-slate-800" />
          ))}
        </div>
      );
    }
    if (error) {
      return (
        <div className="text-center py-12 space-y-3">
          <p className="text-sm text-red-400">{error.message}</p>
          <Button
            size="sm"
            variant="outline"
            onClick={() => refetch()}
            className="border-slate-700 bg-slate-800 hover:bg-slate-700 text-slate-200"
          >
            <RefreshCw className="h-3.5 w-3.5 mr-2" />
            Retry
          </Button>
        </div>
      );
    }
    if (list.length === 0) {
      return (
        <div className="text-center py-16 text-slate-400">
          <CheckCircle2 className="h-10 w-10 mx-auto mb-3 text-slate-600" />
          <p className="text-sm">{emptyMessage}</p>
        </div>
      );
    }
    return (
      <div className="space-y-3">
        {list.map((r) => (
          <RequestCard
            key={r.id}
            request={r}
            onApprove={setApproveTarget}
            onReject={setRejectTarget}
          />
        ))}
      </div>
    );
  };

  return (
    <AdminLayout title="Reactivation Requests">
      <div className="max-w-4xl">
        <Tabs defaultValue="pending" className="w-full">
          <TabsList className="bg-slate-900 border border-slate-800">
            <TabsTrigger
              value="pending"
              className="data-[state=active]:bg-slate-800 data-[state=active]:text-white text-slate-400"
            >
              Pending
              {pending.length > 0 && (
                <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-500 text-slate-950">
                  {pending.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger
              value="reviewed"
              className="data-[state=active]:bg-slate-800 data-[state=active]:text-white text-slate-400"
            >
              Reviewed
            </TabsTrigger>
          </TabsList>

          <TabsContent value="pending" className="mt-4">
            {renderList(pending, "No pending requests. All caught up.")}
          </TabsContent>
          <TabsContent value="reviewed" className="mt-4">
            {renderList(reviewed, "No reviewed requests yet.")}
          </TabsContent>
        </Tabs>
      </div>

      {/* Approve dialog */}
      <Dialog open={!!approveTarget} onOpenChange={(o) => !o && closeApprove()}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100">
          <DialogHeader>
            <DialogTitle className="text-white">
              Approve reactivation for {approveTarget?.company_name}?
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              This will immediately unsuspend the company and restore access for all affected members.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="approve-notes" className="text-slate-300">
              Notes (optional)
            </Label>
            <Textarea
              id="approve-notes"
              value={approveNotes}
              onChange={(e) => setApproveNotes(e.target.value)}
              placeholder="Add any context for the company admin…"
              className="bg-slate-950 border-slate-700 text-slate-100"
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={closeApprove}
              disabled={submitting}
              className="border-slate-700 bg-slate-800 hover:bg-slate-700 text-slate-200"
            >
              Cancel
            </Button>
            <Button
              onClick={handleApprove}
              disabled={submitting}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {submitting ? "Approving…" : "Approve"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={!!rejectTarget} onOpenChange={(o) => !o && closeReject()}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100">
          <DialogHeader>
            <DialogTitle className="text-white">
              Reject reactivation request for {rejectTarget?.company_name}?
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              The company admin will be notified. They cannot resubmit for 24 hours after rejection.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="reject-notes" className="text-slate-300">
              Reason for rejection <span className="text-red-400">*</span>
            </Label>
            <Textarea
              id="reject-notes"
              value={rejectNotes}
              onChange={(e) => setRejectNotes(e.target.value)}
              placeholder="Explain why this request is being rejected…"
              className="bg-slate-950 border-slate-700 text-slate-100"
              rows={4}
              required
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={closeReject}
              disabled={submitting}
              className="border-slate-700 bg-slate-800 hover:bg-slate-700 text-slate-200"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleReject}
              disabled={submitting || !rejectNotes.trim()}
            >
              {submitting ? "Rejecting…" : "Reject"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
